import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Typography
} from '@mui/material';
import {
  NavigateBefore,
  NavigateNext,
  ExpandMore
} from '@mui/icons-material';
import { format } from 'date-fns';

const CustomCalendarHeader = ({
  calendarRef,
  currentView = 'week',
  onViewChange,
  dateRange = null,
  showViewSelector = true
}) => {
  const handleNavigate = (direction) => {
    const calendar = calendarRef?.current?.getApi();
    if (!calendar) return;

    if (direction === 'prev') {
      calendar.prev();
    } else if (direction === 'next') {
      calendar.next();
    } else if (direction === 'today') {
      calendar.today();
    }
  };

  const handleViewChange = (newView) => {
    const calendar = calendarRef?.current?.getApi();
    if (!calendar) return;

    calendar.changeView(newView);
    if (onViewChange) {
      onViewChange(newView);
    }
  };

  const getDateRangeText = () => {
    if (!dateRange) return '';

    const { start, end, view } = dateRange;
    
    if (view === 'dayGridMonth') {
      // In month view, `start` can be from previous month due to leading week days.
      // Use midpoint to display the active month the user navigated to.
      const monthMidpoint = new Date(start);
      monthMidpoint.setDate(monthMidpoint.getDate() + 14);
      return format(monthMidpoint, 'MMMM yyyy');
    } else if (view === 'timeGridWeek') {
      // For week view, use the actual start and end from FullCalendar
      // FullCalendar provides the exact week boundaries
      const weekStart = new Date(start);
      const weekEnd = new Date(end);
      weekEnd.setDate(weekEnd.getDate() - 1); // FullCalendar's end is exclusive
      
      // Format: "December 29 - January 4" or "December 29 - December 31"
      if (format(weekStart, 'MMMM') === format(weekEnd, 'MMMM')) {
        return `${format(weekStart, 'MMMM')} ${format(weekStart, 'd')} - ${format(weekEnd, 'd')}`;
      } else {
        return `${format(weekStart, 'MMMM d')} - ${format(weekEnd, 'MMMM d')}`;
      }
    } else if (view === 'timeGridDay') {
      // For day view, show the single date
      return format(start, 'MMMM d, yyyy');
    }
    
    // Fallback: use provided start and end
    const endDate = new Date(end);
    endDate.setDate(endDate.getDate() - 1); // FullCalendar's end is exclusive
    return `${format(start, 'MMMM d')} - ${format(endDate, 'MMMM d')}`;
  };

  const _getViewLabel = (view) => {
    const viewMap = {
      'dayGridMonth': 'Month',
      'timeGridWeek': 'Week',
      'timeGridDay': 'Day'
    };
    return viewMap[view] || 'Week';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper'
      }}
    >
      {/* Left side: Today button and View selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="text"
          size="small"
          onClick={() => handleNavigate('today')}
          sx={{
            textTransform: 'none',
            minWidth: 'auto',
            px: 1.5,
            py: 0.5,
            fontSize: '0.875rem',
            fontWeight: 400,
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          Today
        </Button>
        
        {showViewSelector && (
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={currentView}
              onChange={(e) => handleViewChange(e.target.value)}
              IconComponent={ExpandMore}
              variant="standard"
              disableUnderline
              sx={{
                fontSize: '0.875rem',
                fontWeight: 400,
                color: 'text.primary',
                '& .MuiSelect-select': {
                  py: 0.5,
                  px: 1,
                  display: 'flex',
                  alignItems: 'center',
                  border: 'none',
                  '&:focus': {
                    backgroundColor: 'transparent'
                  }
                },
                '& .MuiSelect-icon': {
                  fontSize: '1rem',
                  color: 'text.secondary'
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1
                },
                '&:before, &:after': {
                  display: 'none'
                }
              }}
            >
              <MenuItem value="dayGridMonth" sx={{ fontSize: '0.875rem' }}>Month</MenuItem>
              <MenuItem value="timeGridWeek" sx={{ fontSize: '0.875rem' }}>Week</MenuItem>
              <MenuItem value="timeGridDay" sx={{ fontSize: '0.875rem' }}>Day</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Navigation arrows */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleNavigate('prev')}
            sx={{ 
              p: 0.5,
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <NavigateBefore fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleNavigate('next')}
            sx={{ 
              p: 0.5,
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <NavigateNext fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Center: Date range */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 400,
            fontSize: '0.875rem',
            color: 'text.primary'
          }}
        >
          {getDateRangeText()}
        </Typography>
      </Box>

      {/* Right side: Empty for balance */}
      <Box sx={{ width: 200 }} />
    </Box>
  );
};

export default CustomCalendarHeader;

